/**
 * Contenuti del Press Kit (/press-kit). Testi tratti dai contenuti già pubblici del
 * sito (homepage, timeline, Rete Certificata). Regole: nessuna citazione attribuita a
 * persone reali senza un testo fornito dal committente, nessun numero non verificato.
 */
import { timelineEvents } from './timelineData';

export interface DownloadPressKit {
  titolo: string;
  descrizione: string;
  /** Percorso in public/ */
  file: string;
  formato: string;
  /** Anteprima mostrata nella card; se assente si usa il file stesso */
  anteprima?: string;
  /** Fondo dell'anteprima */
  fondo: 'chiaro' | 'scuro';
}

export interface CitazionePressKit {
  testo: string;
  autore: string;
  ruolo: string;
}

export const pressKit = {
  /** Data dell'ultimo aggiornamento della pagina (ISO) */
  aggiornato: '2026-10-22',

  sintesi: {
    nome: 'UNASCA – Unione Nazionale Autoscuole e Studi di Consulenza Automobilistica',
    sede: 'Viale di Val Fiorita 88, 00144 Roma',
    professionisti: 'Oltre 2000 professionisti del settore in tutta Italia',
    guida: [
      { ruolo: 'Segretario Nazionale Studi', nome: 'Giuseppe Guarino' },
      { ruolo: 'Vice Segretario Nazionale Studi', nome: 'Francesco Osquino' }
    ],
    sito: 'https://studi.unasca.it'
  },

  descrizioni: {
    breve:
      "UNASCA, l'Unione Nazionale Autoscuole e Studi di Consulenza Automobilistica nata nel 1967, rappresenta e tutela gli studi di consulenza automobilistica e le autoscuole italiane.",
    paragrafo:
      "Fondata il 25 febbraio 1967, UNASCA è l'associazione di riferimento per gli studi di consulenza automobilistica e le autoscuole italiane. Nel 2004 il Ministero delle Infrastrutture e dei Trasporti l'ha riconosciuta come associazione maggiormente rappresentativa del settore. Offre agli associati formazione professionale attraverso il Centro Studi «Cesare Ferrari», consulenza normativa e operativa e la Rete Certificata UNASCA, la certificazione degli studi con attestato del Poligrafico e Zecca dello Stato.",
    estesa: [
      "UNASCA nasce il 25 febbraio 1967 per rappresentare e tutelare le imprese del settore. Nel corso dei decenni ha accompagnato le tappe che hanno definito la professione: dalla Legge 264/1991, che disciplina l'attività degli studi di consulenza automobilistica, al riconoscimento ministeriale del 2004 come associazione maggiormente rappresentativa, fino al Registro delle Agenzie Telematiche (ReAT) del 2025, che rende obbligatoria la formazione periodica per l'accesso ai servizi del Ministero.",
      "La formazione è da sempre al centro del suo impegno: il Centro Studi «Cesare Ferrari», nato nel 1998, organizza corsi di aggiornamento, seminari e workshop, mentre l'Ente Bilaterale Autoscuole e Studi (EBAS), costituito nel 2012, sostiene la formazione continua di imprese e lavoratori.",
      "Con la Rete Certificata UNASCA gli studi che rispettano requisiti di qualità (conformità normativa, GDPR, sicurezza informatica, assicurazione professionale e formazione continua) ricevono un attestato del Poligrafico e Zecca dello Stato con elementi anticontraffazione e sono consultabili su una mappa pubblica, a tutela del cittadino. Agli associati UNASCA mette a disposizione anche un'area riservata con servizi digitali dedicati alla professione."
    ]
  },

  attivita: [
    'Rappresentanza e tutela degli studi di consulenza automobilistica e delle autoscuole',
    'Formazione professionale con il Centro Studi «Cesare Ferrari»: corsi, seminari e workshop',
    'Consulenza su questioni normative, amministrative e operative del settore',
    'Rete Certificata UNASCA: certificazione degli studi con attestato del Poligrafico e Zecca dello Stato',
    "Servizi digitali nell'area riservata: circolari, svincolo atti, rimborso voucher, community e chat tra associati"
  ],

  /** Vuoto finché il committente non fornisce testi approvati dagli autori. */
  citazioni: [] as CitazionePressKit[],

  contatto: {
    email: 'info@studi.unasca.it',
    telefono: '+39 06.54.22.19.86',
    risposta: 'entro 3 giorni lavorativi'
  },

  colori: [
    { nome: 'Verde UNASCA', hex: '#16a34a', uso: 'Colore principale' },
    { nome: 'Verde scuro', hex: '#15803d', uso: 'Testi e pulsanti' },
    { nome: 'Verde profondo', hex: '#14532d', uso: 'Titoli' },
    { nome: 'Verde notte', hex: '#052e16', uso: 'Fondi scuri' },
    { nome: 'Verde logo', hex: '#4dae66', uso: 'Logo vettoriale' },
    { nome: 'Verde chiaro', hex: '#dcfce7', uso: 'Fondi chiari e accenti' }
  ],

  download: [
    {
      titolo: 'Logo UNASCA',
      descrizione: 'Vettoriale, per stampa e web',
      file: '/kit-stampa/loghi/logo-unasca.svg',
      formato: 'SVG',
      fondo: 'chiaro'
    },
    {
      titolo: 'Logo UNASCA',
      descrizione: 'PNG trasparente, 2000 px',
      file: '/kit-stampa/loghi/logo-unasca-verde-2000.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/loghi/logo-unasca.svg',
      fondo: 'chiaro'
    },
    {
      titolo: 'Logo UNASCA bianco',
      descrizione: 'Per fondi scuri, PNG 2000 px',
      file: '/kit-stampa/loghi/logo-unasca-bianco-2000.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/loghi/logo-unasca-bianco.svg',
      fondo: 'scuro'
    },
    {
      titolo: 'Logo UNASCA compatto',
      descrizione: 'Senza la dicitura per esteso, vettoriale',
      file: '/kit-stampa/loghi/logo-unasca-compatto.svg',
      formato: 'SVG',
      fondo: 'chiaro'
    },
    {
      titolo: 'Logo UNASCA compatto',
      descrizione: 'Senza la dicitura, PNG trasparente 2000 px',
      file: '/kit-stampa/loghi/logo-unasca-compatto-verde-2000.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/loghi/logo-unasca-compatto.svg',
      fondo: 'chiaro'
    },
    {
      titolo: 'Logo UNASCA compatto bianco',
      descrizione: 'Per fondi scuri, PNG 2000 px',
      file: '/kit-stampa/loghi/logo-unasca-compatto-bianco-2000.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/loghi/logo-unasca-compatto-bianco.svg',
      fondo: 'scuro'
    },
    {
      titolo: 'Timbro Rete Certificata',
      descrizione: 'Verde, PNG trasparente 1000 px',
      file: '/kit-stampa/timbri-rete-certificata/timbro-rete-certificata-verde.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/anteprima-timbro-verde.webp',
      fondo: 'chiaro'
    },
    {
      titolo: 'Timbro Rete Certificata',
      descrizione: 'Bianco, per fondi scuri',
      file: '/kit-stampa/timbri-rete-certificata/timbro-rete-certificata-bianco.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/anteprima-timbro-bianco.webp',
      fondo: 'scuro'
    },
    {
      titolo: 'Timbro Rete Certificata',
      descrizione: 'Nero, per stampa in bianco e nero',
      file: '/kit-stampa/timbri-rete-certificata/timbro-rete-certificata-nero.png',
      formato: 'PNG',
      anteprima: '/kit-stampa/anteprima-timbro-nero.webp',
      fondo: 'chiaro'
    }
  ] as DownloadPressKit[],

  foto: [
    {
      titolo: 'Segreteria Nazionale Studi',
      file: '/images/team2.jpg'
    }
  ],

  zip: '/kit-stampa/unasca-studi-press-kit.zip'
};

const TITOLI_TAPPE = [
  'Fondazione di UNASCA',
  'Legge 264/1991',
  'Centro Studi Cesare Ferrari',
  'Riconoscimento ministeriale UNASCA',
  "Costituzione dell'EBAS",
  'REAT e Formazione obbligatoria'
];

/** Tappe della storia per il Press Kit, prese dalla timeline del sito. */
export const tappePressKit = (): { anno: string; titolo: string; testo: string }[] =>
  TITOLI_TAPPE.map((titolo) => timelineEvents.find((e) => e.title === titolo))
    .filter((e): e is NonNullable<typeof e> => Boolean(e))
    .map((e) => ({ anno: e.year, titolo: e.title, testo: e.description }));
